import { virtualize } from "@lit-labs/virtualizer/virtualize.js";
import {
    type DateRange,
    type EmployeeSortProperty,
    GetTimeReportsParams,
    TimeEntryType,
    type TimeReport,
    aggregateTimeReportItems,
    employeeNameOrderForSort,
    getEmployeeName,
    getRange,
    isAbortError,
    serializeColumOptions,
    timeEntryTypeColor,
    toDurationString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { alert } from "../../../elements/alert-dialog";
import "../../../elements/date-range-picker";
import { app } from "../../../init";
import { LatestRequest } from "../../../lib/latest-request";
import { formatFraction } from "../../../lib/lit-dependent.util";
import { singleton } from "../../../lib/singleton";
import { Routing } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { VirtualizerMixin } from "../../../mixins/virtualizer";
import { shared } from "../../../styles";
import "../employees-filter";
import "./components/batch-hour-adjustment-dialog";
import type { BatchHourAdjustmentDialog } from "./components/batch-hour-adjustment-dialog";

@customElement("ptc-employees-time-all")
export class EmployeesTimeAll extends VirtualizerMixin(Routing(StateMixin(LitElement))) {
    routePattern = /^employees\/all\/time$/;

    get routeTitle() {
        return "Arbeitszeiten: Alle Mitarbeiter";
    }

    @state()
    private _reports: TimeReport[] = [];

    @state()
    private _loading = false;

    private _request = new LatestRequest(this);

    @singleton("ptc-batch-hour-adjustments-dialog")
    private _batchHourAdjustmentDialog: BatchHourAdjustmentDialog;

    protected defaultRange = () => getRange(new Date(), "month");
    protected rangeChanged = () => this.load();

    async load() {
        if (!this.dateRange) {
            return;
        }

        this._loading = true;

        try {
            this._reports = await app.api.getTimeReports(
                new GetTimeReportsParams({
                    ...this.dateRange,
                    filters: app.employeeFilters.filters,
                    resolution: "none",
                    types: [
                        TimeEntryType.Work,
                        TimeEntryType.Vacation,
                        TimeEntryType.Sick,
                        TimeEntryType.ChildSick,
                        TimeEntryType.SickInKUG,
                    ],
                }),
                { signal: this._request.start() }
            );
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _addBatchHourAdjustment() {
        await this._batchHourAdjustmentDialog.show({
            filters: app.employeeFilters.filters,
        });
    }

    static styles = [
        shared,
        css`
            :host {
                display: flex;
                flex-direction: column;
            }

            .scroller {
                flex: 1;
                overflow: auto;
            }

            .company {
                text-align: right;
            }

            table {
                border-collapse: separate;
                display: block;
            }

            thead {
                display: block;
                position: sticky;
                top: 0;
                z-index: 2;
            }

            tbody {
                display: block;
                margin-bottom: 1em;
                box-sizing: border-box;
            }

            tr {
                display: grid;
                grid-template-columns: var(--column-width-employee) 4rem 4rem 6rem 4rem 6rem 4rem 6rem;
                align-items: center;
                width: 100%;
            }

            tbody > tr:not(:last-of-type) {
                border-bottom: solid 1px var(--shade-2);
            }

            th,
            td {
                padding: 0.5em;
                vertical-align: middle;
            }

            thead {
                font-weight: bold;
                background: var(--color-bg);
                text-align: center;
            }

            thead th[scope="col"] {
                text-align: left;
            }

            tbody.total {
                font-weight: bold;

                position: sticky;
                top: 4em;
                z-index: 2;
                background: var(--color-bg);
                border-bottom: solid 1px var(--shade-2);
            }

            .total > tr > * {
                font-size: var(--font-size-larger);
            }
        `,
    ];

    render() {
        const reports = this._filterAndSortReports();
        const totalSundays = reports.reduce((sum, r) => sum + (r.sundays || 0), 0);
        const total = aggregateTimeReportItems(
            reports.flatMap((r) => r.items),
            totalSundays
        );

        return html`
            <div class="center-aligning horizontal layout padded">
                <ptc-date-range-picker
                    @range-selected=${(e: CustomEvent<DateRange>) => this.go(null, e.detail)}
                    .range=${this.dateRange}
                    maxDays=${366}
                ></ptc-date-range-picker>

                <div class="stretch"></div>

                <button id="addMenuButton" class="transparent slim">
                    <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                </button>

                <ptc-ui-popover for="addMenuButton" placement="bottom" class="popover-menu" data-popover="close">
                    <button @click=${() => this._addBatchHourAdjustment()} class="small">
                        <ptc-ui-icon name="icon.object.cashbook"></ptc-ui-icon>
                        Zeitbuchung
                    </button>
                </ptc-ui-popover>

                <button
                    class="slim transparent"
                    title="Arbeitszeiten als Excel-Datei Exportieren"
                    @click="${() => {
                        this.go("exports/employee-table-excel", {
                            from: this.dateRange?.from,
                            to: this.dateRange?.to,
                            "entity-filters": JSON.stringify(app.employeeFilters.filters),
                            columns: serializeColumOptions([
                                { type: "fullId" },
                                { type: "timeReportDays" },
                                { type: "timeReportSundays" },
                                { type: "timeReportHours" },
                                { type: "vacationReportDays" },
                                { type: "vacationReportHours" },
                                { type: "sickReportDays" },
                                { type: "sickReportHours" },
                            ]),
                        });
                    }}"
                >
                    <ptc-ui-icon name="icon.file.excel"></ptc-ui-icon>
                </button>

                <button
                    class="transparent slim right-margined"
                    title="Arbeitszeiten Exportieren"
                    @click=${() =>
                        this.go("exports/time-sheet", {
                            "entity-filters": JSON.stringify(app.employeeFilters.filters),
                            ...this.dateRange,
                        })}
                >
                    <ptc-ui-icon name="icon.action.download"></ptc-ui-icon>
                </button>
            </div>

            <ptc-employees-filter
                class="border-top border-bottom"
                @change=${() => this.load()}
                .sortableProps=${["firstName", "lastName", "staffNumber"] as EmployeeSortProperty[]}
            ></ptc-employees-filter>

            <div class="scroller">
                <div class="double-padded">
                    <table class="fill-horizontally">
                        <thead>
                            <tr>
                                <th></th>
                                <th scope="colgroup" colspan="3" style="grid-column: span 3;">
                                    <ptc-ui-icon
                                        name="${app.localized.timeEntryTypeIcon(TimeEntryType.Work)}"
                                    ></ptc-ui-icon>
                                    Arbeit
                                </th>
                                <th
                                    scope="colgroup"
                                    class="number colored-text"
                                    colspan="2"
                                    style="--color-highlight: ${timeEntryTypeColor(
                                        TimeEntryType.Vacation
                                    )}; grid-column: span 2;"
                                >
                                    <ptc-ui-icon
                                        name="${app.localized.timeEntryTypeIcon(TimeEntryType.Vacation)}"
                                    ></ptc-ui-icon>
                                    Urlaub
                                </th>
                                <th
                                    scope="colgroup"
                                    class="number colored-text"
                                    colspan="2"
                                    style="--color-highlight: ${timeEntryTypeColor(
                                        TimeEntryType.Sick
                                    )}; grid-column: span 2;"
                                >
                                    <ptc-ui-icon
                                        name="${app.localized.timeEntryTypeIcon(TimeEntryType.Sick)}"
                                    ></ptc-ui-icon>
                                    Krank
                                </th>
                            </tr>
                            <tr>
                                <th></th>
                                <th scope="col" class="smaller">Tage</th>
                                <th scope="col" class="smaller">Sonntage</th>
                                <th scope="col" class="smaller">Stunden</th>
                                <th scope="col" class="smaller">Tage</th>
                                <th scope="col" class="smaller">Stunden</th>
                                <th scope="col" class="smaller">Tage</th>
                                <th scope="col" class="smaller">Stunden</th>
                            </tr>
                        </thead>

                        <tbody class="total">
                            <tr>
                                <th scope="row"><ptc-ui-icon name="icon.feature.employees"></ptc-ui-icon> Gesamt</th>
                                <td>${formatFraction(total.work.days || 0)}</td>
                                <td>${total.work.sundays || 0}</td>
                                <td>${toDurationString(total.work.hours)}</td>
                                <td>${formatFraction(total.vacation.days || 0)}</td>
                                <td>${toDurationString(total.vacation.hours)}</td>
                                <td>${formatFraction(total.sick.days || 0)}</td>
                                <td>${toDurationString(total.sick.hours)}</td>
                            </tr>
                        </tbody>

                        <tbody data-virtualized>
                            ${virtualize({
                                items: reports,
                                renderItem: (report) => {
                                    const employee = app.getEmployee(report.employeeId);
                                    if (!employee) {
                                        return html``;
                                    }

                                    const { work, vacation, sick } = aggregateTimeReportItems(
                                        report.items,
                                        report.sundays
                                    );

                                    return html`
                                        <tr
                                            class="click"
                                            @click=${() => this.go(`employees/${employee.id}/time`, this.dateRange!)}
                                        >
                                            <th>
                                                <div class="horizontal center-aligning spacing layout employee-header">
                                                    <ptc-ui-avatar
                                                        class="tinier"
                                                        .employee=${employee}
                                                        .positionColors=${employee.positions.map((p) =>
                                                            app.company.getPositionColor(p)
                                                        )}
                                                    ></ptc-ui-avatar>
                                                    <div class="stretch ellispis collapse">
                                                        <span class="semibold">
                                                            ${getEmployeeName(
                                                                employee,
                                                                employeeNameOrderForSort(
                                                                    app.employeeFilters.sortProperty
                                                                )
                                                            )}
                                                        </span>
                                                        ${employee.staffNumber
                                                            ? html`
                                                                  <span class="subtle">#${employee.staffNumber}</span>
                                                              `
                                                            : ""}
                                                    </div>
                                                </div>
                                            </th>
                                            <td>${formatFraction(work.days)}</td>
                                            <td>${work.sundays || 0}</td>
                                            <td>${toDurationString(work.hours)}</td>
                                            <td>${formatFraction(vacation.days)}</td>
                                            <td>${toDurationString(vacation.hours)}</td>
                                            <td>${formatFraction(sick.days)}</td>
                                            <td>${toDurationString(sick.hours)}</td>
                                        </tr>
                                    `;
                                },
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }

    private _filterAndSortReports(): TimeReport[] {
        const filteredEmployeeOrder = new Map(
            app.getFilteredEmployees(this.dateRange ?? undefined).map((e, index) => [e.id, index])
        );

        return this._reports
            .filter((r) => filteredEmployeeOrder.has(r.employeeId))
            .sort((a, b) => {
                return (filteredEmployeeOrder.get(a.employeeId) ?? 0) - (filteredEmployeeOrder.get(b.employeeId) ?? 0);
            });
    }
}
